import axios from "axios";

/**
 * The GUIv=2 API renders ALL responses in camelCase (the rest of
 * ortex-gui is built around that). The options-platform components +
 * types in this codebase were written assuming snake_case, so every
 * multi-word field (`opt_type`, `pop_buyer_pct`, `net_gex`, `call_wall`,
 * `at_expiry`, …) arrived as `undefined`. Rather than rewrite every
 * type + component to camelCase, we deep-normalise each options
 * response back to snake_case at this boundary. Single-word fields are
 * unaffected.
 *
 * `_snake` converts one key; `deepSnake` walks objects + arrays.
 *
 * Digit boundaries matter: the backend camelizer turns `iv_52w_high`
 * into `iv52wHigh` (the underscore before a digit is swallowed), so
 * splitting on capitals alone gave `iv52w_high` back — every
 * digit-containing key (`pop_15_pct`, `iv_5d_change_pct`,
 * `si_5d_change_pct`, …) silently arrived as undefined. Restore the
 * underscore at every letter→digit boundary too; no backend key uses
 * a digit glued to its preceding word.
 */
const _snake = (k: string): string =>
    k.replace(/([A-Z])/g, "_$1").replace(/([a-zA-Z])(\d)/g, "$1_$2").toLowerCase();

function deepSnake<T = unknown>(input: T): T {
    if (Array.isArray(input)) {
        return input.map((v) => deepSnake(v)) as unknown as T;
    }
    if (input !== null && typeof input === "object") {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
            out[_snake(k)] = deepSnake(v);
        }
        return out as unknown as T;
    }
    return input;
}

/**
 * Controllers for the ORTEX Options Platform addon.
 *
 * Backend lives in `web/payment/views.py` (`OptionsPlatformPlansView`,
 * `OptionsPlatformSubscribeView`). The flow mirrors the Index Rebalance
 * Forecasts addon path: try to charge the saved card; if there's no card
 * on file (or SCA is needed) the backend returns a Stripe Checkout URL
 * we then redirect to.
 *
 * Spec ref: coption-specs-for-claude/17-paywall-and-access.md.
 */

export interface OptionsPlatformRecurring {
    interval: string;
    intervalCount: number;
    aggregateUsage?: string | null;
    meter?: string | null;
    trialPeriodDays?: number | null;
    usageType?: string | null;
}

export interface OptionsPlatformPrice {
    currency: string; // display symbol e.g. "$", "£", "€", "kr"
    currencyRequested?: string;
    price: string; // formatted amount for the default (yearly) price
    priceId: string;
    prices?: Array<{
        currency: string;
        priceId: string;
        amount: number;
        recurring: OptionsPlatformRecurring | null;
    }>;
}

export interface OptionsPlatformLaunchOffer {
    discount_percent: number;
    duration_months: number;
    /** False means the coupon isn't currently active in Stripe (backend resolver
     * couldn't find it); UI should suppress promo copy in that case. */
    active: boolean;
}

export interface OptionsPlatformPlanResponse {
    plan: OptionsPlatformPrice;
    /** Optional — older servers won't include it; treat absent as "no offer". */
    launch_offer?: OptionsPlatformLaunchOffer;
}

export interface OptionsPlatformSubscribeResponse {
    purchaseComplete?: boolean;
    checkoutUrl?: string;
    error?: string;
}

/**
 * Fetch the Options Platform addon price in the user's currency.
 *
 * Dev-fallback shape (non-Vercel envs) mirrors index-rebal.ts so the
 * unlock dialog renders something during local development. The
 * canonical prices are configured in
 * `web/payment/management/commands/setup_options_platform_addon.py`.
 */
export const getOptionsPlatformPlan = async (): Promise<OptionsPlatformPlanResponse> => {
    try {
        if (!process.env.NEXT_PUBLIC_VERCEL_ENV) {
            // Dev fallback — price IDs are placeholders that match nothing
            // in Stripe but let the UI render.
            return {
                plan: {
                    currencyRequested: "USD",
                    prices: [
                        {
                            currency: "USD",
                            priceId: "price_dev_options_platform_yearly",
                            amount: 99.0,
                            recurring: {
                                aggregateUsage: null,
                                interval: "year",
                                intervalCount: 1,
                                meter: null,
                                trialPeriodDays: null,
                                usageType: "licensed",
                            },
                        },
                        {
                            currency: "USD",
                            priceId: "price_dev_options_platform_monthly",
                            amount: 9.9,
                            recurring: {
                                aggregateUsage: null,
                                interval: "month",
                                intervalCount: 1,
                                meter: null,
                                trialPeriodDays: null,
                                usageType: "licensed",
                            },
                        },
                    ],
                    currency: "$",
                    priceId: "price_dev_options_platform_yearly",
                    price: "99",
                },
            };
        }
        const res = await axios.get<OptionsPlatformPlanResponse>("/pay/api/options_platform/plans/");
        return res.data;
    } catch (error) {
        throw new Error("Failed to get options platform plan");
    }
};

/**
 * Start a subscription on the user's saved card. If the backend returns
 * a checkout URL (no card on file / SCA needed) we redirect there.
 *
 * The Django backend (`payment/views.py::OptionsPlatformSubscribeView`)
 * returns `purchase_complete` and `checkout_url` in snake_case — there
 * is no global axios snake→camel transform, so we normalise here. Older
 * code on this branch read `res.data.checkoutUrl` and `res.data.purchaseComplete`
 * directly, which never matched and silently broke the entire subscribe
 * flow (the dialog would just sit after a successful payment).
 */
export const subscribeOptionsPlatform = async (
    priceId: string,
): Promise<OptionsPlatformSubscribeResponse> => {
    const res = await axios.post("/pay/api/options_platform/subscribe/", { price_id: priceId });
    const data = res.data ?? {};
    const normalised: OptionsPlatformSubscribeResponse = {
        purchaseComplete: data.purchase_complete ?? data.purchaseComplete ?? false,
        checkoutUrl: data.checkout_url ?? data.checkoutUrl,
        error: data.error,
    };
    if (normalised.checkoutUrl) {
        window.location.href = normalised.checkoutUrl;
    }
    return normalised;
};

// ─────────────────────────────────────────────────────────────────────────────
// Analytics endpoints — back the Options Platform Phase-1 UI components.
// All hit /interface/api/options/* and are gated server-side by
// IsOptionsPlatformSubscriber.
// ─────────────────────────────────────────────────────────────────────────────

export interface ChainStrikeRow {
    strike: number;
    opt_type: "c" | "p";
    bid: number | null;
    ask: number | null;
    last: number | null;
    volume: number | null;
    oi: number | null;
    iv: number | null;
    greeks: {
        delta: number;
        gamma: number;
        theta: number;
        vega: number;
        rho: number;
    } | null;
    pop_buyer_pct: number | null;
    pop_seller_pct: number | null;
    intrinsic: number;
    // 1-5 star tastytrade-style liquidity rating computed from volume,
    // open interest, and bid-ask spread. Always present; defaults to 1
    // for contracts with no scoring data.
    liquidity_stars?: number;
    as_of_date: string;
}

export interface ChainWithGreeksResponse {
    strikes: ChainStrikeRow[];
    underlying: number | null;
    underlying_source: string;
    expiry: string;
    dte: number;
    r: number;
    q: number;
}

export const getChainWithGreeks = async (
    tradingItemId: number,
    expiry: string,
    spot?: number | null,
): Promise<ChainWithGreeksResponse> => {
    const params: Record<string, string | number> = { expiry };
    if (spot != null && spot > 0) params.spot = spot;
    const res = await axios.get<ChainWithGreeksResponse>(
        `/interface/api/options/tradingitem/${tradingItemId}/chain_with_greeks`,
        { params },
    );
    return deepSnake<ChainWithGreeksResponse>(res.data);
};

export interface PnLCurveLegInput {
    opt_type: "c" | "p";
    strike: number;
    expiry_in_years: number;
    /** ISO expiry date — UI-side key for per-leg expiries (calendars /
     *  diagonals): which chain the strike list / repricing comes from.
     *  Stripped before the API call; the wire truth stays expiry_in_years. */
    expiry?: string;
    sigma: number;
    side: "long" | "short";
    qty: number;
    entry_price: number;
}

export interface PnLCurvePoint {
    price: number;
    pnl: number;
    // Per-point Greeks (populated on `today` and `analysis_date` curves,
    // not on `at_expiry`). Drives the Analysis-Mode hover tooltip on the
    // P&L diagram — the cursor reads the row at the nearest grid index.
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
    rho?: number;
}

export interface PnLCurveResponse {
    curves: {
        at_expiry: PnLCurvePoint[];
        today: PnLCurvePoint[];
        analysis_date: PnLCurvePoint[];
    };
    summary: {
        max_profit: number | null;
        max_loss: number | null;
        breakevens: number[];
        pop_pct: number;
        // POP at intermediate horizons (close before expiry).
        pop_15_pct?: number | null;
        pop_30_pct?: number | null;
        pop_45_pct?: number | null;
        pop_60_pct?: number | null;
        // Probability the underlying *touches* the breakeven before
        // expiry (2× approximation). Populated only for defined-risk
        // shapes with two breakevens straddling spot.
        prob_touch_upper_pct?: number | null;
        prob_touch_lower_pct?: number | null;
        // Return on capital — max profit / |max loss|, percent. None for
        // unbounded strategies.
        roc_pct?: number | null;
        // P50 — probability of reaching ≥50% max profit at expiry
        // (tastytrade's "manage at 50% winners" probability).
        pop_half_max_pct?: number | null;
        // Strategy extrinsic value at current spot, per contract.
        // Positive=debit position (paid for time), negative=credit
        // (received time premium).
        extrinsic_value?: number;
        // Buying-power effect (capital required) for defined-risk shapes;
        // None when unbounded.
        bp_effect?: number | null;
        net_credit: number;
        greeks: {
            delta: number;
            gamma: number;
            theta: number;
            vega: number;
            rho: number;
        };
    };
    inputs_echo: {
        r: number;
        q: number;
        iv_factor: number;
        grid_points: number;
    };
}

export interface ExpectedMoveResponse {
    expiry: string;
    expected_move: number | null;
    atm_strike: number | null;
    spot?: number;
    components: {
        atm_strike: number;
        atm_straddle: number | null;
        first_otm_strangle: number | null;
        second_otm_strangle: number | null;
    } | null;
    caveat: string | null;
}

/**
 * Tastytrade-style ±1 SD expected move for one expiry, weighted as
 *   0.6 * ATM straddle + 0.3 * 1st OTM strangle + 0.1 * 2nd OTM strangle.
 * Surfaced as a shaded band on the analyze-page P&L diagram.
 */
export const getExpectedMove = async (
    tradingItemId: number,
    expiry: string,
    spot?: number,
) => {
    const params: Record<string, unknown> = { expiry };
    if (spot != null) params.spot = spot;
    const res = await axios.get<ExpectedMoveResponse>(
        `/interface/api/options/tradingitem/${tradingItemId}/expected_move`,
        { params },
    );
    return deepSnake<ExpectedMoveResponse>(res.data);
};

export const postPnLCurve = async (body: {
    legs: PnLCurveLegInput[];
    underlying_price: number;
    price_range?: { min: number; max: number };
    target_date_in_years?: number;
    iv_factor?: number;
    grid_points?: number;
}): Promise<PnLCurveResponse> => {
    const res = await axios.post<PnLCurveResponse>(
        "/interface/api/options/pnl_curve",
        // Strip the UI-only `expiry` key off each leg — the engine's wire
        // contract is per-leg expiry_in_years.
        { ...body, legs: body.legs.map(({ expiry: _expiry, ...rest }) => rest) },
    );
    return deepSnake<PnLCurveResponse>(res.data);
};

export interface OIHistoryRow {
    date: string;
    call_oi: number;
    put_oi: number;
}
export const getOIHistory = async (tradingItemId: number, days = 30) => {
    const res = await axios.get<{ history: OIHistoryRow[]; days_requested: number }>(
        `/interface/api/options/tradingitem/${tradingItemId}/oi_history`,
        { params: { days } },
    );
    return deepSnake<{ history: OIHistoryRow[]; days_requested: number }>(res.data);
};

export interface GEXStrike {
    strike: number;
    call_oi: number;
    put_oi: number;
    call_gex: number;
    put_gex: number;
    net_gex: number;
}
export interface GEXResponse {
    spot: number;
    spot_source: string;
    net_gex: number;
    call_wall: number | null;
    put_wall: number | null;
    gamma_flip: number | null;
    per_strike: GEXStrike[];
}
export const getGEX = async (tradingItemId: number, spot?: number | null): Promise<GEXResponse> => {
    const params: Record<string, string | number> = {};
    if (spot != null && spot > 0) params.spot = spot;
    const res = await axios.get<GEXResponse>(
        `/interface/api/options/tradingitem/${tradingItemId}/gex`,
        { params },
    );
    return deepSnake<GEXResponse>(res.data);
};

export interface IVRankResult {
    tradingitem_id: number;
    /** Backend now joins the symbol; older clients gracefully fall back. */
    ticker?: string;
    iv_current: number | null;
    iv_rank: number | null;
    iv_percentile: number | null;
    iv_52w_high: number | null;
    iv_52w_low: number | null;
    iv_5d_change_pct: number | null;
    iv_30d_change_pct: number | null;
    /** 52-week daily ATM-IV (30d tenor) series — [iso_date, iv] pairs. */
    history?: [string, number][];
}

export const getIVRank = async (tradingItemId: number) => {
    const res = await axios.get<IVRankResult>(
        `/interface/api/options/tradingitem/${tradingItemId}/iv_rank`,
    );
    return deepSnake<IVRankResult>(res.data);
};

export const getIVScreener = async (params: {
    min_iv_rank?: number;
    max_iv_rank?: number;
    min_iv_pct?: number;
    max_iv_pct?: number;
    limit?: number;
}) => {
    const res = await axios.get<{ results: IVRankResult[]; count: number }>(
        "/interface/api/options/iv_screener",
        { params },
    );
    return deepSnake<{ results: IVRankResult[]; count: number }>(res.data);
};

export interface SuggestedLeg {
    strike: number;
    opt_type: "c" | "p";
    delta: number;
    iv: number;
    bid: number | null;
    ask: number | null;
    side: "long" | "short";
}
export const postStrategySuggest = async (body: {
    tradingitem_id: number;
    expiry: string;
    strategy_id: string;
}) => {
    const res = await axios.post<{
        strategy_id: string;
        expiry: string;
        spot: number;
        spot_source: string;
        legs: SuggestedLeg[];
    }>("/interface/api/options/strategy_suggest", body);
    return deepSnake<{
        strategy_id: string;
        expiry: string;
        spot: number;
        spot_source: string;
        legs: SuggestedLeg[];
    }>(res.data);
};

export interface EarningsIVCrushResponse {
    next_earnings: string | null;
    expiry?: string;
    spot?: number;
    spot_source?: string;
    atm_strike?: number;
    straddle_mid?: number;
    implied_move_pct: number | null;
    /** Average realised 30d-ATM-IV drop across recent earnings (%). */
    historical_crush_pct: number | null;
    historical_crush_events?: {
        earnings_date: string;
        iv_before: number;
        iv_after: number;
        crush_pct: number;
    }[];
    strategy_backtest: unknown | null;
    note?: string;
}
export const getEarningsIVCrush = async (tradingItemId: number, spot?: number | null) => {
    const params: Record<string, number> = {};
    if (spot != null && spot > 0) params.spot = spot;
    const res = await axios.get<EarningsIVCrushResponse>(
        `/interface/api/options/tradingitem/${tradingItemId}/earnings_iv_crush`,
        { params },
    );
    return deepSnake<EarningsIVCrushResponse>(res.data);
};

export interface TermStructurePoint {
    expiry: string;
    dte: number;
    atm_iv: number;
}
export interface TermStructureResponse {
    points: TermStructurePoint[];
    shape: "contango" | "backwardation" | "mixed";
    as_of: string;
}
export const getTermStructure = async (tradingItemId: number) => {
    const res = await axios.get<TermStructureResponse>(
        `/interface/api/options/tradingitem/${tradingItemId}/term_structure`,
    );
    return deepSnake<TermStructureResponse>(res.data);
};

export interface MaxPainResponse {
    max_pain: number | null;
    per_strike: { strike: number; writer_loss: number }[];
    // The expiry actually used — echoes the request param, or the
    // nearest-future default the backend picked when none was sent.
    expiry?: string | null;
}
export const getMaxPain = async (tradingItemId: number, expiry?: string) => {
    const params: Record<string, string> = {};
    if (expiry) params.expiry = expiry;
    const res = await axios.get<MaxPainResponse>(
        `/interface/api/options/tradingitem/${tradingItemId}/max_pain`,
        { params },
    );
    return deepSnake<MaxPainResponse>(res.data);
};

export interface SqueezeAlert {
    tradingitem_id: number;
    ticker: string;
    si_pct: number | null;
    si_5d_change_pct: number | null;
    last_call_sweep_at: string | null;
    last_call_sweep_premium: number;
    last_call_sweep_strike: number | null;
    last_call_sweep_expiry: string | null;
}
export interface SqueezeAlertsResponse {
    alerts: SqueezeAlert[];
    count: number;
}
export const getSqueezeAlerts = async () => {
    const res = await axios.get<SqueezeAlertsResponse>("/interface/api/options/squeeze_alerts");
    return deepSnake<SqueezeAlertsResponse>(res.data);
};

export interface IsEtfResponse {
    ticker: string;
    is_etf: boolean;
    stock_type: number | null;
}

/**
 * Per-ticker "is this an ETF?" lookup. Used by the Flow page's `#OIT`
 * WebSocket handler: when the user has set the standard DataGrid
 * `stock_type` filter, the handler calls this on each unseen ticker so
 * it can mirror the filter on live trades. The caller caches the answer
 * for the session — there should be at most one request per ticker.
 */
// ── Saved strategies (analyzer Save/Load) ───────────────────────────
// Dedicated CRUD API (interface/api/options/strategy_store_views.py),
// mirroring the saved-backtests pattern. The payload is opaque to the
// backend — the analyzer owns its shape.

export interface SavedStrategyPayload {
    legs: PnLCurveLegInput[];
    contracts: number;
    underlying: number;
    dte: number;
    iv: number;
    width: number;
    strategy_id?: string | null;
    ticker?: string | null;
    expiry?: string | null;
}

export interface SavedStrategy {
    id: number;
    name: string;
    ticker: string;
    payload: SavedStrategyPayload;
    created_at: string;
    updated_at: string;
}

export const listSavedStrategies = async (): Promise<SavedStrategy[]> => {
    const res = await axios.get<SavedStrategy[]>("/interface/api/options/strategies");
    return deepSnake<SavedStrategy[]>(res.data);
};

export const createSavedStrategy = async (
    name: string,
    payload: SavedStrategyPayload,
    ticker?: string | null,
): Promise<SavedStrategy> => {
    const res = await axios.post<SavedStrategy>("/interface/api/options/strategies", {
        name,
        ticker: ticker ?? "",
        payload,
    });
    return deepSnake<SavedStrategy>(res.data);
};

export const renameSavedStrategy = async (id: number, name: string): Promise<SavedStrategy> => {
    const res = await axios.patch<SavedStrategy>(`/interface/api/options/strategies/${id}`, { name });
    return deepSnake<SavedStrategy>(res.data);
};

export const deleteSavedStrategy = async (id: number): Promise<void> => {
    await axios.delete(`/interface/api/options/strategies/${id}`);
};

export const getIsEtf = async (ticker: string) => {
    const res = await axios.get<IsEtfResponse>("/interface/api/options/is_etf", { params: { ticker } });
    return deepSnake<IsEtfResponse>(res.data);
};

// ── Dealer positioning + Squeeze Potential Score ────────────────────
// Nightly-computed history (compute_options_positioning). Advanced-
// gated server-side: lower tiers get `gated: true` + a coarse band and
// NO numbers — the UI renders the blurred-teaser upsell from that.

export interface PositioningPoint {
    date: string;
    spot: number | null;
    net_gex: number | null;
    gamma_flip: number | null;
    call_wall: number | null;
    put_wall: number | null;
    max_pain: number | null;
}

export interface PositioningHistoryResponse {
    gated: boolean;
    required?: string;
    history: PositioningPoint[];
    latest: PositioningPoint | null;
}

export const getPositioningHistory = async (
    tradingItemId: number,
    days = 180,
): Promise<PositioningHistoryResponse> => {
    const res = await axios.get<PositioningHistoryResponse>(
        `/interface/api/options/tradingitem/${tradingItemId}/positioning_history`,
        { params: { days } },
    );
    return deepSnake<PositioningHistoryResponse>(res.data);
};

export type SqueezeBand = "low" | "elevated" | "high";

export interface SqueezeComponent {
    key: string;
    label: string;
    raw: number | null;
    unit: string;
    /** Normalised 0-100 sub-score; null = no data for this component. */
    score: number | null;
    weight: number;
    /** Weighted points this component adds to the final score. */
    contribution: number;
    detail: string;
}

export interface SqueezeScoreLatest {
    date: string;
    score: number;
    short_score: number | null;
    options_score: number | null;
    band: SqueezeBand;
    components: SqueezeComponent[];
}

export interface SqueezeScoreResponse {
    gated: boolean;
    required?: string;
    /** Teaser-only fields on gated responses. */
    band?: SqueezeBand | null;
    as_of?: string | null;
    latest: SqueezeScoreLatest | null;
    history: Array<[string, number]>;
}

export const getSqueezeScore = async (tradingItemId: number, days = 180): Promise<SqueezeScoreResponse> => {
    const res = await axios.get<SqueezeScoreResponse>(
        `/interface/api/options/tradingitem/${tradingItemId}/squeeze_score`,
        { params: { days } },
    );
    return deepSnake<SqueezeScoreResponse>(res.data);
};
